import React, { useMemo, useState, useEffect } from 'react';
import { Badge, Text, VStack } from '@chakra-ui/react';
import PlanTypeTabs from './PlanTypeTabs';
import PlanComparisonMatrixPanel from './PlanComparisonMatrixPanel';
import { isLifetimePlanRecord } from './subscriptionPlanMatrixContext';
import { filterPlansForSubscriptionTable } from '../../../utils/planDisplayUtils';

const EMAIL_PACKS_HINT =
	"You can always add additional 'Monthly Email Campaign Sent' packs or one-time top-ups once you've used all your monthly allowance. No need to upgrade your plan.";

/**
 * Subscription vs lifetime plan picker — same tabbed layout as SubscribeToPlanFirstTimePage.
 */
const PlanUpgradePicker = ({
	plans = [],
	isYearly,
	onBillingCycleChange,
	onSelectSubscriptionPlan,
	onSelectLifetimePlan,
	isLoading = false,
	stickyHeaderOffset = '0px',
	firstTimeSubscribe = true,
	showMoneyBackGuarantee = false,
	currentPlanId,
	subscriptionPriceId,
	planMongoId,
	currentPlanName,
	currentPlanPrice,
	subscriptionStatus,
	showFooterHint = true,
	tabsMb = 4,
	subscriptionMatrixProps = {},
	planTab: planTabProp,
	defaultPlanTab = 'subscription',
	onPlanTabChange,
}) => {
	const [internalPlanTab, setInternalPlanTab] = useState(defaultPlanTab);
	const planTab = planTabProp ?? internalPlanTab;

	const setPlanTab = (nextTab) => {
		if (planTabProp === undefined) {
			setInternalPlanTab(nextTab);
		}
		onPlanTabChange?.(nextTab);
	};

	useEffect(() => {
		if (planTabProp === undefined) {
			setInternalPlanTab(defaultPlanTab);
		}
	}, [defaultPlanTab, planTabProp]);

	const tablePlanContext = useMemo(
		() => ({
			subscriptionPriceId,
			planMongoId,
			planId: planMongoId,
			currentPlanName,
			isYearly,
		}),
		[subscriptionPriceId, planMongoId, currentPlanName, isYearly],
	);

	const visiblePlans = useMemo(
		() => filterPlansForSubscriptionTable(plans, tablePlanContext),
		[plans, tablePlanContext],
	);

	const subscriptionPlans = useMemo(
		() => visiblePlans.filter((plan) => !isLifetimePlanRecord(plan)),
		[visiblePlans],
	);
	const lifetimePlans = useMemo(
		() => visiblePlans.filter((plan) => isLifetimePlanRecord(plan)),
		[visiblePlans],
	);

	const hasSubscriptionPlans = subscriptionPlans.length > 0;
	const hasLifetimePlans = lifetimePlans.length > 0;
	const showPlanTypeTabs =
		isLoading || hasSubscriptionPlans === hasLifetimePlans;

	let effectivePlanTab = planTab;
	if (!isLoading) {
		if (planTab === 'subscription' && !hasSubscriptionPlans && hasLifetimePlans) {
			effectivePlanTab = 'lifetime';
		} else if (planTab === 'lifetime' && !hasLifetimePlans && hasSubscriptionPlans) {
			effectivePlanTab = 'subscription';
		}
	}

	useEffect(() => {
		if (effectivePlanTab !== planTab) {
			setPlanTab(effectivePlanTab);
		}
	}, [effectivePlanTab, planTab]);

	return (
		<VStack spacing={4} align='stretch' w='full'>
			{showPlanTypeTabs && (
				<PlanTypeTabs planTab={effectivePlanTab} onChange={setPlanTab} mb={tabsMb} />
			)}

			{effectivePlanTab === 'subscription' ? (
				<PlanComparisonMatrixPanel
					plans={subscriptionPlans}
					isYearly={isYearly}
					onBillingCycleChange={onBillingCycleChange}
					onSelectPlan={onSelectSubscriptionPlan}
					isLoading={isLoading}
					stickyHeaderOffset={stickyHeaderOffset}
					firstTimeSubscribe={firstTimeSubscribe}
					headerTitle='Compare subscription plans'
					headerHint={EMAIL_PACKS_HINT}
					currentPlanId={currentPlanId}
					subscriptionPriceId={subscriptionPriceId}
					planMongoId={planMongoId}
					currentPlanName={currentPlanName}
					currentPlanPrice={currentPlanPrice}
					subscriptionStatus={subscriptionStatus}
					showMoneyBackGuarantee={showMoneyBackGuarantee}
					matrixProps={subscriptionMatrixProps}
				/>
			) : (
				<PlanComparisonMatrixPanel
					plans={lifetimePlans}
					isYearly={false}
					onSelectPlan={onSelectLifetimePlan}
					isLoading={isLoading}
					showBillingToggle={false}
					stickyHeaderOffset={stickyHeaderOffset}
					firstTimeSubscribe={firstTimeSubscribe}
					headerTitle='Compare lifetime plans'
					pricingMode='lifetime'
					currentPlanId={currentPlanId}
					subscriptionPriceId={subscriptionPriceId}
					planMongoId={planMongoId}
					currentPlanName={currentPlanName}
					currentPlanPrice={currentPlanPrice}
					subscriptionStatus={subscriptionStatus}
					headerExtra={
						<Badge colorScheme='orange' borderRadius='full' px={3}>
							One-time payment
						</Badge>
					}
				/>
			)}

			{showFooterHint ? (
				<Text fontSize='xs' color='gray.500' textAlign='center' m={0} px={2}>
					{effectivePlanTab === 'subscription'
						? 'Subscriptions can be upgraded, downgraded, or canceled from settings.'
						: 'Lifetime plans are a one-time purchase through Stripe checkout.'}
				</Text>
			) : null}
		</VStack>
	);
};

export default PlanUpgradePicker;
